import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { type ComponentProps, type HTMLAttributes, type ReactNode } from 'react';
import { type DefaultTooltipContentProps, ResponsiveContainer, type TooltipValueType } from 'recharts';

import { Div } from '@ui/components/base/div';
import { cn } from '@ui/lib/utils';

const INITIAL_DIMENSION = { width: 320, height: 200 } as const;

const HIDDEN_TOOLTIP_TYPE = 'none';

const CHART_CONTAINER_CLASSNAME = [
  'aspect-video justify-center text-xs',
  '[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground',
  '[&_.recharts-layer]:outline-hidden',
  '[&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted',
  '[&_.recharts-surface]:outline-hidden',
].join(' ');

const TOOLTIP_CLASSNAME = [
  'min-w-32 items-start gap-1.5 rounded-lg border border-border/50',
  'bg-background px-2.5 py-1.5 text-xs shadow-xl',
].join(' ');

type TooltipNameType = number | string;

interface ChartContainerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  children: ComponentProps<typeof ResponsiveContainer>['children'];
  initialDimension?: {
    width: number;
    height: number;
  };
}

const ChartContainer = ({
  className,
  children,
  initialDimension = INITIAL_DIMENSION,
  ...props
}: ChartContainerProps) => (
  <Div row data-slot="chart" className={cn(CHART_CONTAINER_CLASSNAME, className)} {...props}>
    <ResponsiveContainer initialDimension={initialDimension}>{children}</ResponsiveContainer>
  </Div>
);

interface ChartTooltipContentProps
  extends Pick<DefaultTooltipContentProps<TooltipValueType, TooltipNameType>, 'label' | 'labelFormatter' | 'payload'>,
    Required<Pick<DefaultTooltipContentProps<TooltipValueType, TooltipNameType>, 'formatter'>> {
  active?: boolean;
  className?: string;
  renderFooter?: (payload: NonNullable<ChartTooltipContentProps['payload']>) => ReactNode;
}

const ChartTooltipContent = ({
  active,
  payload,
  label,
  labelFormatter,
  formatter,
  className,
  renderFooter,
}: ChartTooltipContentProps) => {
  if (!active || isNil(payload) || isEmpty(payload)) {
    return null;
  }

  const tooltipLabel = isNil(labelFormatter) ? label : labelFormatter(label, payload);

  return (
    <Div grid className={cn(TOOLTIP_CLASSNAME, className)}>
      {!isNil(tooltipLabel) && <Div className="font-medium">{tooltipLabel}</Div>}
      <Div grid className="gap-1.5">
        {payload.reduce(
          (rows: ReactNode[], item, index) =>
            item.type === HIDDEN_TOOLTIP_TYPE
              ? rows
              : [
                  ...rows,
                  <Div key={item.name} row className="w-full flex-wrap items-center gap-2">
                    {formatter(item.value, item.name, item, index, payload)}
                  </Div>,
                ],
          []
        )}
      </Div>
      {renderFooter?.(payload)}
    </Div>
  );
};

export { ChartContainer, ChartTooltipContent };
